import {useCallback, useLayoutEffect, useMemo, useRef} from 'react';
import {Formik, setIn} from 'formik';
import {gql, useApolloClient} from '@apollo/client';
import {parseGraphQLError, translate} from '../../utils';
import useGlobalSpinner from '../../hooks/useGlobalSpinner';
import { AddGuestsStaticProvider } from './AddGuestsContext';
import { AddGuestsContentHeader } from './AddGuestsContentHeader';
import { AddGuestsContent } from './AddGuestsContent';

export default function AddGuestsContentWrapper({
    backUrl,
    reservationCode,
    guestColumnKeys,
    guestColumnKeysMap,
    reservationRooms,
    clientTitlesMap,
    countriesMap,
    initialRegionMaps,
    languageMap,
    vipStatusMap,
    defaultGuestCountryCode,
    defaultGuestLanguage,
}) {
    const apolloClient = useApolloClient();

    const guestsInitialValues = useRef(null);
    if (guestsInitialValues.current === null) {
        guestsInitialValues.current = buildGuestsInitialValues(reservationRooms, guestColumnKeys);
    }

    const handleRegisterGuestInitialValues = useCallback((clientKey, personFieldValues) => {
        if (guestsInitialValues.current[clientKey] !== undefined) return;

        guestsInitialValues.current[clientKey] = { ...personFieldValues };
    }, []);

    const handleFormSubmit = useCallback(async (values, formik) => {
        formik.setStatus(undefined);

        try {
            const removeGuestOperations = [];
            const createGuestOperations = [];
            const addGuestOperations = [];
            const updateGuestOperations = new Map();

            for (let roomIndex = 0; roomIndex < values.rooms.length; roomIndex++) {
                const room = values.rooms[roomIndex];
                const roomId = room.roomId;

                const initialRoomGuests = (reservationRooms[roomIndex].guests).filter((guest) => guest.clientKey !== '');
                const submittedClientKeys = new Set();
                for (const guest of room.guests) {
                    if (guest.clientKey !== '') submittedClientKeys.add(guest.clientKey);
                }
                const initialRoomGuestsToRemove = initialRoomGuests.filter((initialGuest) => initialGuest.clientKey !== '' && !submittedClientKeys.has(initialGuest.clientKey));

                for (const guest of initialRoomGuestsToRemove) {
                    removeGuestOperations.push({ roomId, clientId: guest.clientKey });
                }

                const initialRoomGuestKeys = new Set(initialRoomGuests.map((initialGuest) => initialGuest.clientKey));

                room.guests.forEach((roomGuest, guestIndex) => {
                    if (!guestHasData(roomGuest, guestColumnKeys)) return;

                    if (roomGuest.clientKey === '') {
                        const personInput = guestToCreatePersonInput(
                            roomGuest,
                            defaultGuestCountryCode,
                            defaultGuestLanguage,
                        );
                        createGuestOperations.push({ roomId, personInput, roomIndex, guestIndex });
                    } else {
                        const wasClientInitiallyInThisRoom = initialRoomGuestKeys.has(roomGuest.clientKey);
                        if (!wasClientInitiallyInThisRoom) {
                            addGuestOperations.push({ roomId, clientId: roomGuest.clientKey, roomIndex, guestIndex });
                        }

                        const guestInitialValues = guestsInitialValues.current[roomGuest.clientKey];
                        if (isGuestUpdated(roomGuest, guestInitialValues)) {
                            const personInput = guestToUpdatePersonInput(
                                roomGuest,
                                defaultGuestCountryCode,
                                defaultGuestLanguage,
                            );
                            updateGuestOperations.set(roomGuest.clientKey, personInput);
                        }
                    }
                });
            }

            const mutations = [];
            const mutationsMetas = [];

            const addMutation = (mutation, variables, meta) => {
                mutationsMetas.push(meta);
                mutations.push(apolloClient.mutate({ mutation, variables }));
            };

            for (const { roomId, clientId } of removeGuestOperations) {
                addMutation(
                    REMOVE_ROOM_STAY_GUEST_MUTATION,
                    { input: { roomStayId: roomId, clientId } },
                    { mutationKind: 'remove' },
                );
            }

            for (const createGuestOperation of createGuestOperations) {
                const chainOperationLeadingMutationIndex = mutations.length;

                addMutation(
                    CREATE_CLIENT_MUTATION,
                    { input: { person: createGuestOperation.personInput } },
                    {
                        mutationKind: 'createAndAdd',
                        roomIndex: createGuestOperation.roomIndex,
                        guestIndex: createGuestOperation.guestIndex,
                    },
                );

                addMutation(
                    ADD_ROOM_STAY_GUEST_MUTATION,
                    { roomStayId: createGuestOperation.roomId },
                    {
                        mutationKind: 'createAndAdd',
                        roomIndex: createGuestOperation.roomIndex,
                        guestIndex: createGuestOperation.guestIndex,
                        chainOperationLeadingMutationIndex,
                    },
                );
            }

            for (const operation of addGuestOperations) {
                addMutation(
                    ADD_ROOM_STAY_GUEST_MUTATION,
                    { roomStayId: operation.roomId, clientId: operation.clientId },
                    {
                        mutationKind: 'add',
                        roomIndex: operation.roomIndex,
                        guestIndex: operation.guestIndex,
                    },
                );
            }

            for (const [clientKey, personInput] of updateGuestOperations.entries()) {
                addMutation(
                    UPDATE_CLIENT_MUTATION,
                    { input: { id: clientKey, person: personInput } },
                    { mutationKind: 'update', clientKey },
                );
            }

            const graphqlErrors = buildInitialGraphqlErrors();
            if (mutations.length > 0) {
                const linkedGuestsMap = buildLinkedGuestsMap(values.rooms);
                const mutationsResults = await Promise.allSettled(mutations);

                for (let mutationIndex = 0; mutationIndex < mutationsResults.length; mutationIndex++) {
                    const mutationResult = mutationsResults[mutationIndex];

                    if (mutationResult.status === 'rejected') {
                        const meta = mutationsMetas[mutationIndex];

                        if (meta.chainOperationLeadingMutationIndex !== undefined) {
                            const chainOperationLeadingMutationResult = mutationsResults[meta.chainOperationLeadingMutationIndex];
                            if (chainOperationLeadingMutationResult.status === 'rejected') {
                                continue;
                            }
                        }

                        if (meta.mutationKind === 'remove') {
                            addGraphqlError(mutationResult.reason, () => null, graphqlErrors);
                        } else if (meta.mutationKind === 'createAndAdd' || meta.mutationKind === 'add') {
                            addGraphqlError(mutationResult.reason, formFieldLookupCallback(meta.roomIndex, meta.guestIndex), graphqlErrors);
                        } else if (meta.mutationKind === 'update') {
                            const linkedGuests = linkedGuestsMap.get(meta.clientKey) ?? [];

                            for (const linkedGuest of linkedGuests) {
                                addGraphqlError(mutationResult.reason, formFieldLookupCallback(linkedGuest.roomIndex, linkedGuest.guestIndex), graphqlErrors);
                            }
                        }
                    }
                }
            }

            if (graphqlErrors.hasErrors) {
                showGraphqlErrors(formik, graphqlErrors);
            } else {
                window.location.href = backUrl
            }
        } catch (error) {
            const graphqlErrors = buildInitialGraphqlErrors();
            addGraphqlError(error, () => null, graphqlErrors);
            showGraphqlErrors(formik, graphqlErrors);
        }
    }, [apolloClient, backUrl, defaultGuestCountryCode, defaultGuestLanguage, guestColumnKeys, reservationRooms]);

    const addGuestsStaticContextValue = useMemo(
        () => ({
            backUrl,
            reservationCode,
            guestColumnKeys,
            guestColumnKeysMap,
            clientTitlesMap,
            countriesMap,
            initialRegionMaps,
            languageMap,
            vipStatusMap,
            roomsCount: reservationRooms.length,
            maxGuestsPerRoom: 10,
            onRegisterGuestInitialValues: handleRegisterGuestInitialValues,
        }),
        [
            backUrl,
            reservationCode,
            guestColumnKeys,
            guestColumnKeysMap,
            clientTitlesMap,
            countriesMap,
            initialRegionMaps,
            languageMap,
            vipStatusMap,
            reservationRooms,
            handleRegisterGuestInitialValues,
        ],
    );

    return (
        <AddGuestsStaticProvider value={addGuestsStaticContextValue}>
            <Formik
                initialValues={{ rooms: reservationRooms }}
                validate={validateAddGuestsForm}
                onSubmit={handleFormSubmit}
                validateOnChange={false}
                validateOnBlur={false}
            >
                {(formikProps) => (
                    <AddGuestsFormikBody {...formikProps} />
                )}
            </Formik>
        </AddGuestsStaticProvider>
    );
}

function AddGuestsFormikBody({
    dirty,
    errors,
    isSubmitting,
    values: { rooms },
    setErrors,
    setFieldValue,
    setStatus: setFormikStatus,
    submitForm,
    status: formikStatus,
}) {
    const [, setGlobalSpinnerLoading] = useGlobalSpinner(false);

    useLayoutEffect(() => {
        setGlobalSpinnerLoading(isSubmitting);
    }, [isSubmitting, setGlobalSpinnerLoading]);

    return (
        <>
            <AddGuestsContentHeader dirty={dirty} isSubmitting={isSubmitting} />

            <AddGuestsContent
                errors={errors}
                rooms={rooms}
                setErrors={setErrors}
                setFieldValue={setFieldValue}
                setFormikStatus={setFormikStatus}
                submitForm={submitForm}
                formikStatus={formikStatus}
            />
        </>
    );
}

function validateAddGuestsForm(values) {
    let errors = {};
    const duplicateClientMessage = translate('frontend', 'ADD_GUESTS_DUPLICATE_CLIENT');

    values.rooms.forEach((room, roomIndex) => {
        const guestKeysToIndices = new Map();

        room.guests.forEach((guest, guestIndex) => {
            if (guest.clientKey === '') return;

            if (guestKeysToIndices.has(guest.clientKey)) {
                const duplicateGuestIndex = guestKeysToIndices.get(guest.clientKey);

                errors = setIn(
                    errors,
                    `rooms.${roomIndex}.guests.${duplicateGuestIndex}.clientKey`,
                    duplicateClientMessage,
                );

                errors = setIn(
                    errors,
                    `rooms.${roomIndex}.guests.${guestIndex}.clientKey`,
                    duplicateClientMessage,
                );
            }

            guestKeysToIndices.set(guest.clientKey, guestIndex);
        });
    });

    return errors;
}

function buildInitialGraphqlErrors() {
    return {
        hasErrors: false,
        fieldErrors: {},
        unknownErrors: [],
        hadParseError: false,
    };
}

function addGraphqlError(error, fieldLookupCallback, graphqlErrors) {
    graphqlErrors.hasErrors = true;

    try {
        const [fieldErrors, unknownErrors] = parseGraphQLError(error, fieldLookupCallback);

        for (const [fieldPath, messages] of Object.entries(fieldErrors)) {
            graphqlErrors.fieldErrors = setIn(graphqlErrors.fieldErrors, fieldPath, messages[0]);
        }

        graphqlErrors.unknownErrors.push(...unknownErrors);
    } catch (_) {
        graphqlErrors.hadParseError = true;
    }
}

function showGraphqlErrors(formik, graphqlErrors) {
    if (graphqlErrors.hadParseError) {
        graphqlErrors.unknownErrors.push(translate('frontend', 'ERROR_INPROCESS'));
    }

    const status = graphqlErrors.unknownErrors.length > 0 ? { unknownErrors: graphqlErrors.unknownErrors } : undefined; // Errors are shown as unknown errors above the table

    formik.setFormikState((previousFormikState) => ({
        ...previousFormikState,
        errors: graphqlErrors.fieldErrors,
        status,
    }));
}

function formFieldLookupCallback(roomIndex, guestIndex) {
    return (argumentPath) => {
        const graphqlField = argumentPath[2];
        const formFieldKey = graphqlField === 'preferences' ? 'stayPreferences' : graphqlField;

        return `rooms.${roomIndex}.guests.${guestIndex}.${formFieldKey}`;
    };
}

function buildLinkedGuestsMap(rooms) {
    const linkedGuestsMap = new Map();

    rooms.forEach((room, roomIndex) => {
        room.guests.forEach((roomGuest, guestIndex) => {
            if (roomGuest.clientKey === '') return;

            const linkedGuests = linkedGuestsMap.get(roomGuest.clientKey) ?? [];
            linkedGuests.push({ roomIndex, guestIndex });
            linkedGuestsMap.set(roomGuest.clientKey, linkedGuests);
        });
    });

    return linkedGuestsMap;
}

function buildGuestsInitialValues(reservationRooms, guestColumnKeys) {
    const guestsInitialValues = {};

    for (const reservationRoom of reservationRooms) {
        for (const guest of reservationRoom.guests) {
            if (guest.clientKey === '') { // Filters out empty guests
                continue;
            }

            guestsInitialValues[guest.clientKey] = extractGuestPersonFieldValues(guest, guestColumnKeys);
        }
    }

    return guestsInitialValues;
}

function extractGuestPersonFieldValues(guest, guestColumnKeys) {
    const personFieldValues = { clientKey: guest.clientKey };

    for (const guestColumnKey of guestColumnKeys) {
        personFieldValues[guestColumnKey] = guest[guestColumnKey];
    }

    return personFieldValues;
}

function guestHasData(guest, guestColumnKeys) {
    for (const key of guestColumnKeys) {
        const value = guest[key];

        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return true;
        }
    }
    return false;
}

function guestToCreatePersonInput(
    guest,
    defaultGuestCountryCode,
    defaultGuestLanguage,
) {
    return {
        clientTitleId: guest.clientTitleId || undefined,
        firstname: (guest.firstname ?? '').trim() || undefined,
        lastname: (guest.lastname ?? '').trim() || undefined,
        street: (guest.street ?? '').trim() || undefined,
        zipcode: (guest.zipcode ?? '').trim() || undefined,
        city: (guest.city ?? '').trim() || undefined,
        country: guest.country || defaultGuestCountryCode,
        language: guest.language || defaultGuestLanguage,
        email: (guest.email ?? '').trim() || undefined,
        telephone: (guest.telephone ?? '').trim() || undefined,
        mobile: (guest.mobile ?? '').trim() || undefined,
        fax: (guest.fax ?? '').trim() || undefined,
        stayPreferences: (guest.stayPreferences ?? '').trim() || undefined,
        mealPreferences: (guest.mealPreferences ?? '').trim() || undefined,
        nationality: guest.nationality || undefined,
        passportNumber: (guest.passportNumber ?? '').trim() || undefined,
        idCardNumber: (guest.idCardNumber ?? '').trim() || undefined,
        issuingAuthority: (guest.issuingAuthority ?? '').trim() || undefined,
        birthday: guest.birthday || undefined,
        carPlateNumber: (guest.carPlateNumber ?? '').trim() || undefined,
        regionId: guest.regionId || undefined,
        vipStatusId: guest.vipStatusId || undefined,
        comments: (guest.comments ?? '').trim() || undefined,
    };
}

function guestToUpdatePersonInput(
    guest,
    defaultGuestCountryCode,
    defaultGuestLanguage,
) {
    return {
        clientTitleId: guest.clientTitleId || null,
        firstname: (guest.firstname ?? '').trim(),
        lastname: (guest.lastname ?? '').trim(),
        street: (guest.street ?? '').trim(),
        zipcode: (guest.zipcode ?? '').trim(),
        city: (guest.city ?? '').trim(),
        country: guest.country || defaultGuestCountryCode,
        language: guest.language || defaultGuestLanguage,
        email: (guest.email ?? '').trim(),
        telephone: (guest.telephone ?? '').trim(),
        mobile: (guest.mobile ?? '').trim(),
        fax: (guest.fax ?? '').trim(),
        stayPreferences: (guest.stayPreferences ?? '').trim(),
        mealPreferences: (guest.mealPreferences ?? '').trim(),
        nationality: guest.nationality || null,
        passportNumber: (guest.passportNumber ?? '').trim(),
        idCardNumber: (guest.idCardNumber ?? '').trim(),
        issuingAuthority: (guest.issuingAuthority ?? '').trim(),
        birthday: guest.birthday || null,
        carPlateNumber: (guest.carPlateNumber ?? '').trim(),
        regionId: guest.regionId || null,
        vipStatusId: guest.vipStatusId || null,
        comments: (guest.comments ?? '').trim(),
    };
}

function isGuestUpdated(currentGuestValues, initialGuestValues) {
    for (const key of Object.keys(initialGuestValues)) {
        if (currentGuestValues[key] !== initialGuestValues[key]) {
            return true;
        }
    }

    return false;
}

const CREATE_CLIENT_MUTATION = gql`
    mutation CreateClient($input: CreateClientInput!) {
        createClient(input: $input) {
            client {
                id @chain(variable: "clientId")
            }
        }
    }
`;

const UPDATE_CLIENT_MUTATION = gql`
    mutation UpdateClient($input: UpdateClientInput!) {
        updateClient(input: $input) {
            client {
                id
            }
        }
    }
`;

const ADD_ROOM_STAY_GUEST_MUTATION = gql`
    mutation AddRoomStayGuest($roomStayId: ID!, $clientId: ID!) {
        addRoomStayGuest(input: { roomStayId: $roomStayId, clientId: $clientId }) {
            roomStay {
                id
            }
        }
    }
`;

const REMOVE_ROOM_STAY_GUEST_MUTATION = gql`
    mutation RemoveRoomStayGuest($input: RemoveRoomStayGuestInput!) {
        removeRoomStayGuest(input: $input) {
            roomStay {
                id
            }
        }
    }
`;
